/**
 * Viewer preferences: theme, density, whether the filter controls are shown, and
 * which filters the bar shows.
 *
 * All are per-person conveniences, so they live in localStorage rather than anywhere
 * server-side. Every read and write is wrapped — a private window, cleared site data or
 * a browser set to block storage all throw on access, and the app has to render
 * correctly with no stored value.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

export type Theme = 'system' | 'light' | 'dark'
export type Density = 'compact' | 'comfortable' | 'relaxed'
/** The wide-screen page rail: names and icons, icons only, or out of the way. */
export type RailMode = 'full' | 'icons' | 'hidden'

const RAIL_KEY = 'cpc.rail.v1'

const THEME_KEY = 'cpc.theme'
const DENSITY_KEY = 'cpc.density'
/** Whether the filter bar's controls are shown at all. The scope row always is. */
const FILTERS_OPEN_KEY = 'cpc.filtersOpen.v1'
/**
 * Which filters the bar shows. Stored as the HIDDEN keys, so a filter added later
 * shows by default. Empty = everything shown.
 */
const FILTERS_SHOWN_KEY = 'cpc.filtersShown.v1'

/** Only an array of strings is trusted; anything else means nothing hidden. */
function readHidden(): string[] {
  try {
    const raw = localStorage.getItem(FILTERS_SHOWN_KEY)
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []
  } catch {
    return []
  }
}

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* preference is a convenience; failing to persist it is not an error */
  }
}

interface Prefs {
  theme: Theme
  density: Density
  filtersOpen: boolean
  /** Filter keys the bar does not show. A hidden filter keeps its value and still applies. */
  hiddenFilters: string[]
  railMode: RailMode
  setRailMode: (m: RailMode) => void
  setTheme: (t: Theme) => void
  setDensity: (d: Density) => void
  setFiltersOpen: (open: boolean) => void
  setHiddenFilters: (keys: string[]) => void
}

const Ctx = createContext<Prefs | null>(null)

export function usePreferences(): Prefs {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>')
  return ctx
}

export const THEMES: { value: Theme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'Auto' },
  { value: 'dark', label: 'Dark' },
]

/** Ordered tightest to loosest, which is how the control reads left to right. */
export const DENSITIES: { value: Density; label: string; note: string }[] = [
  { value: 'compact', label: 'Compact', note: 'The spec density — most rows per screen' },
  { value: 'comfortable', label: 'Comfortable', note: 'The default — 15px prose' },
  { value: 'relaxed', label: 'Relaxed', note: 'Largest type; wide tables scroll sooner' },
]

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() =>
    read(THEME_KEY, ['system', 'light', 'dark'] as const, 'system'),
  )
  const [density, setDensityState] = useState<Density>(() =>
    read(DENSITY_KEY, ['compact', 'comfortable', 'relaxed'] as const, 'comfortable'),
  )
  // Default open: a first visit should show what can be filtered.
  const [filtersOpen, setFiltersOpenState] = useState<boolean>(
    () => read(FILTERS_OPEN_KEY, ['open', 'closed'] as const, 'open') === 'open',
  )
  const [hiddenFilters, setHiddenFiltersState] = useState<string[]>(readHidden)
  // Default full: a first visit should see every page by name.
  const [railMode, setRailModeState] = useState<RailMode>(() =>
    read(RAIL_KEY, ['full', 'icons', 'hidden'] as const, 'full'),
  )
  const setRailMode = useCallback((m: RailMode) => {
    setRailModeState(m)
    write(RAIL_KEY, m)
  }, [])

  // 'system' stamps nothing, so prefers-color-scheme decides — matching how the tokens
  // are structured in styles/tokens.css.
  useEffect(() => {
    const root = document.documentElement
    if (theme === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', theme)
  }, [theme])

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density)
  }, [density])

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t)
    write(THEME_KEY, t)
  }, [])

  const setDensity = useCallback((d: Density) => {
    setDensityState(d)
    write(DENSITY_KEY, d)
  }, [])

  const setFiltersOpen = useCallback((open: boolean) => {
    setFiltersOpenState(open)
    write(FILTERS_OPEN_KEY, open ? 'open' : 'closed')
  }, [])

  const setHiddenFilters = useCallback((keys: string[]) => {
    setHiddenFiltersState(keys)
    write(FILTERS_SHOWN_KEY, JSON.stringify(keys))
  }, [])

  return (
    <Ctx.Provider
      value={{
        theme,
        density,
        filtersOpen,
        hiddenFilters,
        railMode,
        setRailMode,
        setTheme,
        setDensity,
        setFiltersOpen,
        setHiddenFilters,
      }}
    >
      {children}
    </Ctx.Provider>
  )
}
